export type CalculatorCategory =
  | "Math & Algebra"
  | "Grade & Education"
  | "Finance & Everyday"
  | "Health & Fitness"
  | "Unit & Physics"
  | "Conversion & Geometry"
  | "Statistics & Data"
  | "Engineering & Electronics"
  | "Chemistry & Science"
  | "Business & Productivity";

export interface CalculatorMeta {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  category: CalculatorCategory;
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const CALCULATOR_CATEGORIES: CalculatorCategory[] = [
  "Math & Algebra",
  "Grade & Education",
  "Finance & Everyday",
  "Health & Fitness",
  "Unit & Physics",
  "Conversion & Geometry",
  "Statistics & Data",
  "Engineering & Electronics",
  "Chemistry & Science",
  "Business & Productivity",
];

export const CALCULATORS: CalculatorMeta[] = [
  {
    slug: "matrix-calculator",
    name: "Matrix Calculator",
    tagline: "Solve 2x2 & 3x3 matrix determinant, inverse, transpose, addition, & multiplication.",
    description:
      "Free online matrix calculator. Compute 2x2 and 3x3 determinants, inverse matrices, transpose, addition, subtraction, and matrix multiplication with step-by-step solutions.",
    category: "Math & Algebra",
    icon: "Grid",
    featured: true,
    keywords: [
      "matrix calculator",
      "determinant calculator",
      "inverse matrix calculator",
      "matrix multiplication calculator",
      "3x3 determinant",
      "matrix transpose",
      "2x2 matrix inverse",
    ],
  },
  {
    slug: "logarithm-calculator",
    name: "Logarithm & Exponential Calculator",
    tagline: "Calculate logᵦ(x), natural log ln(x), log₁₀(x) and solve bˣ = y.",
    description:
      "Free online logarithm solver. Calculate logarithms with any base, natural log ln, common log, and solve exponential equations with step-by-step change of base formulas.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "logarithm calculator",
      "log calculator",
      "natural log calculator",
      "ln calculator",
      "log base 2 calculator",
      "exponential equation solver",
      "change of base formula",
    ],
  },
  {
    slug: "lcm-gcd-calculator",
    name: "LCM & GCD (HCF) Calculator",
    tagline: "Calculate Least Common Multiple, Greatest Common Divisor & Prime Factors.",
    description:
      "Find LCM and GCD (HCF) for multiple numbers instantly. Includes prime factorization breakdown and step-by-step Euclidean algorithm solutions.",
    category: "Math & Algebra",
    icon: "Hash",
    featured: true,
    keywords: [
      "lcm calculator",
      "gcd calculator",
      "hcf calculator",
      "least common multiple",
      "greatest common divisor",
      "prime factorization calculator",
      "euclidean algorithm steps",
    ],
  },
  {
    slug: "trigonometry-calculator",
    name: "Trigonometry & Right Triangle Solver",
    tagline: "Solve right triangle sides/angles and evaluate sin, cos, tan, cot, sec, csc.",
    description:
      "Free online trigonometry calculator. Solve right triangles for missing sides and angles, evaluate trigonometric functions in degrees or radians, and view interactive SVG diagrams.",
    category: "Math & Algebra",
    icon: "Compass",
    featured: true,
    keywords: [
      "trigonometry calculator",
      "right triangle solver",
      "sin cos tan calculator",
      "hypotenuse calculator",
      "inverse trig calculator",
      "trig ratio solver",
      "degree to radian calculator",
    ],
  },
  {
    slug: "sequence-calculator",
    name: "Sequence & Series Calculator (AP & GP)",
    tagline: "Solve Arithmetic & Geometric Progressions (n-th term, sum, infinite series).",
    description:
      "Calculate n-th term, sum of first n terms, and infinite geometric series for Arithmetic (AP) and Geometric (GP) progressions with sequence term tables.",
    category: "Math & Algebra",
    icon: "ListOrdered",
    featured: true,
    keywords: [
      "sequence calculator",
      "arithmetic progression calculator",
      "geometric progression calculator",
      "ap gp calculator",
      "nth term calculator",
      "sum of series calculator",
      "infinite geometric series",
    ],
  },
  {
    slug: "percentage-calculator",
    name: "Percentage Calculator",
    tagline: "Calculate percentages, percentage differences & percentage changes instantly.",
    description:
      "Free online percentage calculator with 4 interactive modes. Calculate percentage of a number, percentage increase/decrease, percentage difference, and what percent one number is of another with step-by-step formulas.",
    category: "Math & Algebra",
    icon: "Percent",
    featured: true,
    keywords: [
      "percentage calculator",
      "calculate percentage of a number",
      "percentage difference calculator",
      "percentage increase calculator",
      "percentage change formula",
      "calculate grade percentage",
      "how to find percentage",
      "percent change calculator",
    ],
  },
  {
    slug: "grade-calculator",
    name: "Grade & GPA Calculator",
    tagline: "Calculate weighted grades, course averages, and target exam scores.",
    description:
      "Free weighted grade calculator for students and teachers. Calculate overall course grade, find what score you need on your final exam, and convert letter grades to percentage and GPA.",
    category: "Grade & Education",
    icon: "GraduationCap",
    featured: true,
    keywords: [
      "grade calculator",
      "weighted grade calculator",
      "final exam grade calculator",
      "gpa calculator",
      "calculate overall grade",
      "what grade do i need on final",
      "course grade finder",
      "letter grade to percentage",
    ],
  },
  {
    slug: "fraction-to-percent-calculator",
    name: "Fraction to Percent Calculator",
    tagline: "Convert any fraction into percentage & decimal with step-by-step math.",
    description:
      "Convert improper, proper, and mixed fractions into exact percentages and decimals instantly. Includes step-by-step fraction reduction and visual breakdown.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "fraction to percent calculator",
      "convert fraction to percentage",
      "fraction to decimal and percent",
      "how to turn a fraction into a percent",
      "simplifying fraction calculator",
      "fraction to percent formula",
      "math fraction converter",
      "mixed number to percentage",
    ],
  },
  {
    slug: "attendance-calculator",
    name: "Attendance Percentage Calculator",
    tagline: "Track attendance %, missed classes allowed & target attendance needed.",
    description: "Calculate your current class attendance percentage, find out how many classes you can safely miss to maintain 75% or 85% attendance, or how many classes to attend.",
    category: "Grade & Education",
    icon: "UserCheck",
    featured: true,
    keywords: [
      "attendance percentage calculator",
      "how many classes can i miss calculator",
      "bunk calculator for college",
      "minimum attendance requirement finder",
      "class attendance percentage",
      "attendance percentage calculator for students",
      "calculate attendance percentage",
      "75 percent attendance calculator",
    ],
  },
  {
    slug: "discount-calculator",
    name: "Discount & Sales Tax Calculator",
    tagline: "Calculate sale price, double discounts, tax & total money saved.",
    description: "Free shopping discount calculator. Calculate final price after percentage off, add sales tax, compute stacked double discounts, and see exact total savings.",
    category: "Finance & Everyday",
    icon: "Tag",
    featured: true,
    keywords: [
      "discount calculator",
      "sales tax calculator",
      "calculate discount percentage",
      "shopping discount price calculator",
      "double discount calculator",
      "calculate savings on sale",
      "price after tax and discount",
      "percent off calculator",
    ],
  },
  {
    slug: "age-calculator",
    name: "Exact Age & Birthday Calculator",
    tagline: "Calculate exact age in years, months, days & upcoming birthday countdown.",
    description: "Free exact age calculator by date of birth. Calculate age in years, months, weeks, days, hours, and minutes with next birthday countdown.",
    category: "Finance & Everyday",
    icon: "Calendar",
    featured: true,
    keywords: [
      "exact age calculator",
      "calculate age in months and days",
      "date of birth age calculator",
      "how old am i in days",
      "birthday countdown calculator",
      "age difference calculator",
      "calculate chronological age",
      "age calculator online free",
    ],
  },
  {
    slug: "ratio-calculator",
    name: "Ratio & Proportion Calculator",
    tagline: "Solve ratios, find missing values in A:B = C:D, and simplify ratios.",
    description: "Free ratio and proportion solver. Simplify ratios to lowest terms, solve equivalent proportions (A:B = C:D), and divide an amount into a given ratio.",
    category: "Math & Algebra",
    icon: "Scale",
    featured: true,
    keywords: [
      "ratio calculator",
      "proportion calculator",
      "solve proportion a b c d",
      "simplifying ratio calculator",
      "divide amount in ratio",
      "ratio scaling calculator",
      "equivalent ratio finder",
      "how to solve ratios",
    ],
  },
  {
    slug: "average-calculator",
    name: "Average, Median & Mode Calculator",
    tagline: "Calculate mean, median, mode, range, min, max, and total sum.",
    description: "Calculate statistical average (mean), median, mode, range, and total sum for any list of numbers. Features instant statistical analysis with step-by-step math.",
    category: "Math & Algebra",
    icon: "BarChart3",
    featured: true,
    keywords: [
      "average calculator",
      "mean median mode calculator",
      "find median of numbers",
      "range calculator math",
      "calculate statistical average",
      "find mode in a set of numbers",
      "mean calculator online",
      "number set average finder",
    ],
  },
  {
    slug: "tip-calculator",
    name: "Tip & Bill Splitter Calculator",
    tagline: "Calculate tip percentage, total bill & split cost per person.",
    description: "Free restaurant tip calculator and bill splitter. Calculate custom tip percentages, total bill amount, and split the cost evenly among any number of people.",
    category: "Finance & Everyday",
    icon: "Receipt",
    featured: true,
    keywords: [
      "tip calculator",
      "bill splitter calculator",
      "calculate tip percentage",
      "split restaurant bill per person",
      "tip amount finder",
      "dining bill splitter",
      "how much tip to leave",
      "group meal cost splitter",
    ],
  },
  {
    slug: "time-calculator",
    name: "Time & Work Hours Calculator",
    tagline: "Add or subtract hours, minutes & calculate work timesheet duration.",
    description: "Free time duration calculator. Add or subtract hours and minutes, calculate elapsed time between start and end times, and track work timesheet totals.",
    category: "Finance & Everyday",
    icon: "Clock",
    featured: true,
    keywords: [
      "time calculator",
      "work hours calculator",
      "add hours and minutes",
      "elapsed time calculator",
      "timesheet calculator online",
      "time duration between two times",
      "calculate hours worked",
      "time card calculator",
    ],
  },
  {
    slug: "bmi-calculator",
    name: "BMI & Ideal Weight Calculator",
    tagline: "Calculate Body Mass Index (BMI), health category & ideal weight range.",
    description: "Free Body Mass Index (BMI) calculator for adults and teens. Calculate BMI using metric or imperial units, view health category charts, and find ideal weight ranges.",
    category: "Finance & Everyday",
    icon: "HeartPulse",
    featured: true,
    keywords: [
      "bmi calculator",
      "body mass index calculator",
      "calculate bmi online",
      "ideal body weight calculator",
      "healthy weight range for height",
      "metric bmi calculator",
      "bmi score chart for adults",
      "body weight category finder",
    ],
  },
  {
    slug: "interest-calculator",
    name: "Simple & Compound Interest Calculator",
    tagline: "Calculate interest earned, investment growth & compound schedules.",
    description: "Free simple and compound interest calculator. Calculate investment growth over time with monthly, quarterly, or annual compounding and total interest earned.",
    category: "Finance & Everyday",
    icon: "TrendingUp",
    featured: true,
    keywords: [
      "compound interest calculator",
      "simple interest calculator",
      "investment growth calculator",
      "calculate interest earned",
      "savings interest growth formula",
      "compounding interest schedule",
      "principal plus interest calculator",
      "future value calculator",
    ],
  },
  {
    slug: "cgpa-calculator",
    name: "CGPA to Percentage Calculator",
    tagline: "Convert CGPA to percentage, calculate semester CGPA & grade classification.",
    description: "Free CGPA to percentage calculator. Convert 10-point, 4-point, or custom scale CGPA to exact percentage score and calculate overall semester CGPA.",
    category: "Grade & Education",
    icon: "Award",
    featured: true,
    keywords: [
      "cgpa to percentage calculator",
      "convert cgpa to percent",
      "semester cgpa calculator",
      "cgpa conversion formula",
      "10 point cgpa to percentage",
      "calculate overall cgpa",
      "cgpa into percentage",
      "college cgpa calculator",
    ],
  },
  {
    slug: "scientific-notation-calculator",
    name: "Scientific Notation Calculator",
    tagline: "Convert numbers to scientific notation (E-notation) & standard decimal.",
    description: "Free scientific notation converter. Convert large and small decimal numbers into scientific notation (a × 10^b), engineering notation, and E-notation instantly.",
    category: "Math & Algebra",
    icon: "Atom",
    featured: true,
    keywords: [
      "scientific notation calculator",
      "convert decimal to scientific notation",
      "e notation to decimal converter",
      "scientific notation formula",
      "convert standard form to scientific",
      "physics scientific notation calculator",
      "chemistry exponent calculator",
      "scientific notation converter online",
    ],
  },
  {
    slug: "salary-calculator",
    name: "Salary & Hourly Wage Calculator",
    tagline: "Convert hourly pay to annual salary, monthly earnings & paycheck estimate.",
    description: "Free salary and wage converter. Convert hourly pay rates to weekly, biweekly, monthly, and annual salary with overtime pay calculations.",
    category: "Finance & Everyday",
    icon: "Wallet",
    featured: true,
    keywords: [
      "hourly to salary calculator",
      "convert hourly wage to annual salary",
      "salary calculator monthly",
      "paycheck calculator hourly",
      "calculate annual income from hourly rate",
      "biweekly salary finder",
      "hourly wage converter",
      "work earnings calculator",
    ],
  },
  {
    slug: "gpa-target-calculator",
    name: "Target GPA Calculator",
    tagline: "Find future semester GPA required to reach your target cumulative GPA.",
    description: "Calculate what GPA you need in future semesters to raise your overall cumulative GPA to your target goal (e.g. 3.5 or 3.8 GPA for graduation).",
    category: "Grade & Education",
    icon: "Target",
    featured: true,
    keywords: [
      "target gpa calculator",
      "how to raise my gpa calculator",
      "gpa needed to reach target",
      "cumulative gpa calculator future semesters",
      "calculate required gpa",
      "college target gpa finder",
      "desired gpa calculator",
      "raise gpa to 3.5",
    ],
  },
  {
    slug: "loan-calculator",
    name: "Loan Monthly Payment Calculator",
    tagline: "Calculate monthly loan payments, total interest cost & loan payoff summary.",
    description: "Free personal loan payment calculator. Calculate monthly payments, total interest paid, and total cost of borrowing for car loans, personal loans, or student loans.",
    category: "Finance & Everyday",
    icon: "CreditCard",
    featured: true,
    keywords: [
      "loan payment calculator",
      "calculate monthly loan payment",
      "personal loan interest calculator",
      "car loan monthly payment finder",
      "total loan interest cost",
      "loan repayment schedule calculator",
      "monthly installment calculator",
      "loan payoff calculator",
    ],
  },
  {
    slug: "word-count-calculator",
    name: "Word & Character Count Calculator",
    tagline: "Count words, characters, sentences, paragraphs & estimated reading time.",
    description: "Free online word counter and text statistics tool. Count words, characters with/without spaces, sentences, paragraphs, reading time, and speaking time instantly.",
    category: "Grade & Education",
    icon: "FileText",
    featured: true,
    keywords: [
      "word count calculator",
      "character counter online",
      "calculate reading time",
      "essay word counter",
      "sentence and paragraph counter",
      "word count for student essay",
      "speaking time estimator",
      "text stats calculator",
    ],
  },
  {
    slug: "gpa-to-percentage-calculator",
    name: "GPA to Percentage Converter",
    tagline: "Convert 4.0 GPA scale to percentage marks & letter grade equivalence.",
    description: "Free GPA to percentage converter. Convert 4.0 scale GPA, weighted GPA, and college GPA scores into exact percentage marks with formula step-by-step.",
    category: "Grade & Education",
    icon: "GraduationCap",
    featured: true,
    keywords: [
      "gpa to percentage calculator",
      "convert 4.0 gpa to percent",
      "gpa percentage conversion formula",
      "college gpa to percentage marks",
      "calculate percentage from gpa",
      "convert gpa to percentage grade",
      "4 point gpa percent converter",
      "gpa score to percent scale",
    ],
  },
  {
    slug: "pythagorean-theorem-calculator",
    name: "Pythagorean Theorem Calculator",
    tagline: "Calculate hypotenuse (c) or legs (a, b) of a right triangle instantly.",
    description: "Free Pythagorean theorem solver. Calculate hypotenuse c = √(a² + b²) or missing leg length a or b for right triangles with step-by-step formula explanations.",
    category: "Math & Algebra",
    icon: "Triangle",
    featured: true,
    keywords: [
      "pythagorean theorem calculator",
      "calculate hypotenuse right triangle",
      "a2 b2 c2 formula calculator",
      "find missing side of right triangle",
      "pythagoras theorem solver",
      "hypotenuse calculator online",
      "right triangle side length finder",
      "pythagorean equation solver",
    ],
  },
  {
    slug: "quadratic-formula-calculator",
    name: "Quadratic Equation Solver",
    tagline: "Solve ax² + bx + c = 0, find real & complex roots, discriminant & vertex.",
    description: "Free quadratic equation solver. Solve quadratic equations ax² + bx + c = 0 using the quadratic formula x = (-b ± √(b² - 4ac)) / (2a) with step-by-step steps.",
    category: "Math & Algebra",
    icon: "Variable",
    featured: true,
    keywords: [
      "quadratic formula calculator",
      "solve quadratic equation ax2 bx c",
      "find roots of quadratic equation",
      "discriminant calculator b2 4ac",
      "quadratic equation solver step by step",
      "real and complex roots finder",
      "algebra quadratic solver",
      "parabola vertex calculator",
    ],
  },
  {
    slug: "fuel-cost-calculator",
    name: "Fuel Cost & Gas Mileage Calculator",
    tagline: "Calculate trip gas expense, total fuel volume & cost per person.",
    description: "Free fuel cost calculator for road trips and daily commutes. Calculate total gas cost based on trip distance, fuel efficiency (MPG or L/100km), and fuel price.",
    category: "Finance & Everyday",
    icon: "Fuel",
    featured: true,
    keywords: [
      "fuel cost calculator",
      "gas cost calculator for road trip",
      "calculate trip fuel cost",
      "mpg gas cost calculator",
      "road trip gas cost per person",
      "calculate gas money needed",
      "fuel expense estimator",
      "commute fuel calculator",
    ],
  },
  {
    slug: "margin-calculator",
    name: "Profit Margin & Markup Calculator",
    tagline: "Calculate gross profit margin %, markup %, gross profit & revenue.",
    description: "Free gross profit margin and markup calculator. Calculate profit margin percentage, markup percentage, total gross profit, and selling price based on item cost.",
    category: "Finance & Everyday",
    icon: "DollarSign",
    featured: true,
    keywords: [
      "profit margin calculator",
      "gross margin calculator",
      "markup percentage calculator",
      "calculate gross profit margin",
      "cost selling price margin finder",
      "business profit margin formula",
      "margin vs markup calculator",
      "revenue profit margin solver",
    ],
  },
  {
    slug: "pace-calculator",
    name: "Running Pace & Speed Calculator",
    tagline: "Calculate running pace per mile/km, finish time & split times.",
    description: "Free running pace calculator. Calculate pace (min/mile or min/km), total finish time, or distance for 5K, 10K, half marathon, and full marathon races.",
    category: "Finance & Everyday",
    icon: "Timer",
    featured: true,
    keywords: [
      "running pace calculator",
      "calculate pace per mile",
      "5k finish time calculator",
      "half marathon pace finder",
      "marathon finish time calculator",
      "min per mile to min per km converter",
      "running speed calculator",
      "race pace split calculator",
    ],
  },
  {
    slug: "calorie-calculator",
    name: "Daily Calorie & BMR Calculator",
    tagline: "Calculate Basal Metabolic Rate (BMR) & daily maintenance calories.",
    description: "Free daily calorie requirement calculator. Compute BMR using the Mifflin-St Jeor formula and find daily calories needed for weight maintenance, loss, or muscle gain.",
    category: "Health & Fitness",
    icon: "Flame",
    featured: true,
    keywords: [
      "calorie calculator",
      "bmr calculator online",
      "daily maintenance calories calculator",
      "calculate calories for weight loss",
      "mifflin st jeor formula calculator",
      "daily calorie intake finder",
      "macronutrient calorie solver",
      "metabolic rate calculator",
    ],
  },
  {
    slug: "water-intake-calculator",
    name: "Daily Water Intake Calculator",
    tagline: "Calculate recommended daily water intake based on body weight & activity.",
    description: "Free hydration calculator. Calculate how much water you should drink per day in liters, fluid ounces, and glass cups based on weight, climate, and exercise level.",
    category: "Health & Fitness",
    icon: "Droplets",
    featured: true,
    keywords: [
      "water intake calculator",
      "how much water to drink per day",
      "daily hydration calculator",
      "water intake by body weight",
      "liters of water per day finder",
      "fluid ounce water intake calculator",
      "workout water intake requirement",
      "daily water goal calculator",
    ],
  },
  {
    slug: "body-fat-calculator",
    name: "Body Fat Percentage Calculator",
    tagline: "Calculate body fat % using US Navy method & lean body mass.",
    description: "Free body fat percentage calculator. Calculate body fat %, lean body mass, and fat mass using US Navy tape measure circumference measurements.",
    category: "Health & Fitness",
    icon: "Activity",
    featured: true,
    keywords: [
      "body fat percentage calculator",
      "us navy body fat calculator",
      "calculate body fat percentage",
      "lean body mass calculator",
      "body fat percentage formula",
      "fat mass vs muscle mass finder",
      "tape measure body fat solver",
      "ideal body fat percentage chart",
    ],
  },
  {
    slug: "velocity-acceleration-calculator",
    name: "Speed, Velocity & Acceleration Calculator",
    tagline: "Solve kinematic equations: v = u + at, s = ut + ½at².",
    description: "Free physics acceleration and velocity calculator. Solve kinematic motion equations for initial velocity (u), final velocity (v), acceleration (a), time (t), and displacement (s).",
    category: "Unit & Physics",
    icon: "Zap",
    featured: true,
    keywords: [
      "velocity calculator",
      "acceleration calculator physics",
      "kinematic equations solver",
      "calculate final velocity",
      "speed distance time calculator",
      "physics motion solver",
      "acceleration formula calculator",
      "displacement velocity solver",
    ],
  },
  {
    slug: "density-calculator",
    name: "Density, Mass & Volume Calculator",
    tagline: "Calculate material density (ρ = m/V), mass or volume instantly.",
    description: "Free mass, volume, and density solver. Calculate density ρ = m/V, mass m = ρV, or volume V = m/ρ in kg/m³, g/cm³, or lbs/ft³ with unit conversions.",
    category: "Unit & Physics",
    icon: "Box",
    featured: true,
    keywords: [
      "density calculator",
      "mass volume density calculator",
      "calculate density of object",
      "density formula rho m v",
      "find volume from mass and density",
      "g cm3 to kg m3 density converter",
      "physics density solver",
      "chemistry density calculator",
    ],
  },
  {
    slug: "gpa-to-letter-calculator",
    name: "Percentage to Letter Grade Converter",
    tagline: "Convert percentage test marks to letter grade & GPA points.",
    description: "Free score grade converter. Convert percentage marks (0-100%) into letter grades (A+, A, B, C, D, F) and 4.0 GPA quality points instantly.",
    category: "Grade & Education",
    icon: "CheckCircle2",
    featured: true,
    keywords: [
      "percentage to letter grade converter",
      "convert marks to letter grade",
      "percent to gpa converter",
      "test score letter grade finder",
      "grading scale converter",
      "calculate letter grade from percent",
      "4.0 gpa quality points converter",
      "high school grade converter",
    ],
  },
  {
    slug: "ideal-weight-calculator",
    name: "Ideal Body Weight (IBW) Calculator",
    tagline: "Calculate target healthy ideal weight range using clinical formulas.",
    description: "Free Ideal Body Weight (IBW) calculator. Compute your ideal weight using Devine, Robinson, Miller, and Hamwi medical formulas based on height and gender.",
    category: "Health & Fitness",
    icon: "Heart",
    featured: true,
    keywords: [
      "ideal body weight calculator",
      "calculate ideal weight for height",
      "devine formula ideal weight",
      "healthy target weight range",
      "ibw calculator online",
      "ideal body weight for men and women",
      "medical ideal weight finder",
      "ideal weight chart",
    ],
  },
  {
    slug: "macro-calculator",
    name: "Macronutrient Ratio Calculator",
    tagline: "Calculate daily protein, carb & fat grams for Keto or Fitness goals.",
    description: "Free macro calculator. Calculate daily grams of protein, carbohydrates, and fats needed for your target calorie intake across Keto, Balanced, or High-Protein diets.",
    category: "Health & Fitness",
    icon: "Apple",
    featured: true,
    keywords: [
      "macro calculator",
      "macronutrient calculator online",
      "calculate protein carbs fats grams",
      "keto macro calculator",
      "high protein macro split",
      "daily macro ratio finder",
      "bodybuilding macro solver",
      "flexible dieting macro calculator",
    ],
  },
  {
    slug: "weight-loss-calculator",
    name: "Weight Loss Calculator & Target Date Planner",
    tagline: "Calculate daily calorie deficit, target date & weekly weight loss timeline.",
    description: "Free weight loss calculator. Compute daily calorie deficit required to reach your target weight, estimate completion date, and plan safe weekly weight loss rates.",
    category: "Health & Fitness",
    icon: "TrendingDown",
    featured: true,
    keywords: [
      "weight loss calculator",
      "calorie deficit calculator",
      "weight loss timeline calculator",
      "how long to lose weight calculator",
      "target weight date planner",
      "daily calorie goal for weight loss",
      "safe weight loss rate finder",
      "fat loss calorie calculator",
      "body weight reduction schedule",
    ],
  },
  {
    slug: "height-calculator",
    name: "Child & Adult Height Predictor Calculator",
    tagline: "Predict child's future adult height & estimate growth potential.",
    description: "Free height predictor calculator. Estimate a child's future adult height using Mid-Parental Height (Khamis-Roche) method and track growth benchmarks.",
    category: "Health & Fitness",
    icon: "Ruler",
    featured: true,
    keywords: [
      "height calculator",
      "child height predictor calculator",
      "mid parental height calculator",
      "future adult height estimator",
      "how tall will my child be calculator",
      "khamis roche height predictor",
      "child growth potential solver",
      "parental height genetic formula",
      "predicted adult stature finder",
    ],
  },
  {
    slug: "height-comparison-calculator",
    name: "Height Comparison Calculator (Side by Side)",
    tagline: "Compare human heights side by side with visual scale graphics & difference math.",
    description: "Free side-by-side height comparison calculator. Compare heights between multiple adults, children, or couples visually with height difference in feet, inches, cm, and percentage ratios.",
    category: "Health & Fitness",
    icon: "Ruler",
    featured: true,
    keywords: [
      "height comparison calculator",
      "side by side height comparison",
      "compare height side by side calculator",
      "height difference calculator",
      "visual height comparator tool",
      "height compare couple calculator",
      "how much taller am i calculator",
      "feet and inches height comparison",
      "human height visualizer online",
    ],
  },
  {
    slug: "army-body-fat-calculator",
    name: "US Army Body Fat Calculator",
    tagline: "Calculate military body fat % & compliance using official tape test formulas.",
    description: "Free US Army Body Fat calculator. Compute military body fat percentage and pass/fail status according to AR 600-9 tape test standards for male and female service members.",
    category: "Health & Fitness",
    icon: "ShieldCheck",
    featured: true,
    keywords: [
      "army body fat calculator",
      "us army body fat calculator",
      "ar 600 9 tape test calculator",
      "military body fat percentage",
      "navy tape measure body fat finder",
      "army body composition calculator",
      "service member body fat solver",
      "army tape test standards chart",
      "military body fat pass fail",
    ],
  },
  {
    slug: "keto-calculator",
    name: "Keto Macro & Net Carb Calculator",
    tagline: "Calculate exact daily Net Carbs, Protein, Fats & Calories for Ketosis.",
    description: "Free Keto macro calculator. Compute net carbs, protein, and fat grams required to enter and stay in ketosis for weight loss, fat burning, or therapeutic keto.",
    category: "Health & Fitness",
    icon: "Flame",
    featured: true,
    keywords: [
      "keto calculator",
      "keto macro calculator",
      "net carb calculator for keto",
      "ketogenic diet macro solver",
      "how many net carbs per day keto",
      "keto fat protein carb ratio finder",
      "ketosis macro intake calculator",
      "keto weight loss calorie planner",
      "strict keto macro breakdown",
    ],
  },
  {
    slug: "height-percentile-calculator",
    name: "Height Percentile Calculator (CDC & WHO)",
    tagline: "Calculate height percentile for kids, teens & adults against CDC/WHO charts.",
    description: "Free height percentile calculator. Determine how tall you or your child is compared to global population growth benchmarks using official CDC and WHO growth charts.",
    category: "Health & Fitness",
    icon: "BarChart2",
    featured: true,
    keywords: [
      "height percentile calculator",
      "child height percentile calculator",
      "cdc growth chart height solver",
      "who height percentile finder",
      "what percentile height am i",
      "adult height percentile calculator",
      "height growth curve solver",
      "pediatric height growth chart",
      "height ranking by age and gender",
    ],
  },
  {
    slug: "body-surface-area-calculator",
    name: "Body Surface Area (BSA) Calculator",
    tagline: "Calculate BSA (m²) using Mosteller, DuBois, Haycock & Boyd formulas.",
    description: "Free Body Surface Area (BSA) calculator. Compute total body surface area in square meters (m²) using Mosteller, DuBois, Haycock, Gehan, and Boyd medical formulas.",
    category: "Health & Fitness",
    icon: "Square",
    featured: true,
    keywords: [
      "body surface area calculator",
      "bsa calculator online",
      "mosteller formula bsa calculator",
      "dubois body surface area solver",
      "calculate bsa in m2",
      "medical chemotherapy dosage bsa",
      "haycock bsa formula calculator",
      "body surface area chart",
      "clinical bsa calculation tool",
    ],
  },
  {
    slug: "total-body-water-calculator",
    name: "Total Body Water (TBW) Calculator",
    tagline: "Calculate Total Body Water (liters & %) using Watson & Hume formulas.",
    description: "Free Total Body Water (TBW) calculator. Estimate your body fluid volume in liters and percentage of body mass using clinical Watson, Hume-WEY, and Mellits-Cheek formulas.",
    category: "Health & Fitness",
    icon: "Droplet",
    featured: true,
    keywords: [
      "total body water calculator",
      "tbw calculator online",
      "watson formula total body water",
      "calculate body water percentage",
      "hume formula body water solver",
      "fluid volume hydration calculator",
      "clinical body water content finder",
      "total body water in liters",
      "body fluid composition calculator",
    ],
  },
  {
    slug: "healthy-weight-calculator",
    name: "Healthy Weight Calculator & Range Finder",
    tagline: "Find your optimal healthy weight range based on height & frame size.",
    description: "Free Healthy Weight Calculator. Determine your ideal healthy weight range, healthy BMI boundaries (18.5–24.9), and target weight goals for long-term wellness.",
    category: "Health & Fitness",
    icon: "Activity",
    featured: true,
    keywords: [
      "healthy weight calculator",
      "healthy weight range for height",
      "what is a healthy weight for me",
      "healthy weight range chart",
      "normal bmi weight range finder",
      "optimal body weight calculator",
      "healthy weight goals solver",
      "target healthy weight for age",
      "medical healthy weight range",
    ],
  },
  {
    slug: "ohms-law-calculator",
    name: "Ohm's Law & Power Calculator",
    tagline: "Calculate Voltage (V = I×R), Current (I), Resistance (R) & Power (P).",
    description: "Free Ohm's Law calculator for electronics and physics. Solve Voltage (V), Current (I), Resistance (R), and Power (P = V×I) with unit conversions.",
    category: "Unit & Physics",
    icon: "Cpu",
    featured: true,
    keywords: [
      "ohms law calculator",
      "calculate voltage current resistance",
      "v ir formula calculator",
      "power calculator watts",
      "electrical resistance solver",
      "physics circuit calculator",
      "current velocity power finder",
      "ohms law equation solver",
    ],
  },
  {
    slug: "convert-temperature-calculator",
    name: "Temperature Converter Calculator",
    tagline: "Convert Celsius (°C), Fahrenheit (°F) & Kelvin (K) temperature scales.",
    description: "Free temperature scale converter. Convert between Celsius, Fahrenheit, and Kelvin scales instantly with conversion formulas and step-by-step math.",
    category: "Unit & Physics",
    icon: "Thermometer",
    featured: true,
    keywords: [
      "temperature converter calculator",
      "celsius to fahrenheit converter",
      "fahrenheit to celsius formula",
      "kelvin to celsius calculator",
      "convert c to f",
      "temperature scale converter",
      "physics temperature solver",
      "celsius fahrenheit kelvin chart",
    ],
  },
  {
    slug: "simple-interest-calculator",
    name: "Simple Interest Loan & Savings Calculator",
    tagline: "Calculate simple interest (I = P×r×t), total balance & loan return.",
    description: "Free simple interest calculator. Compute simple interest earned or paid using I = P × r × t, total accrued balance, and annual yield.",
    category: "Finance & Everyday",
    icon: "Coins",
    featured: true,
    keywords: [
      "simple interest calculator",
      "calculate simple interest i prt",
      "simple interest loan formula",
      "savings simple interest growth",
      "principal rate time interest finder",
      "simple interest rate solver",
      "interest yield calculator",
      "total balance simple interest",
    ],
  },
  {
    slug: "percentage-error-calculator",
    name: "Percentage Error Calculator",
    tagline: "Calculate percent error between experimental & accepted values.",
    description: "Free percentage error calculator for physics and chemistry labs. Calculate percentage error |Experimental - Theoretical| / Theoretical × 100%.",
    category: "Math & Algebra",
    icon: "Percent",
    featured: true,
    keywords: [
      "percentage error calculator",
      "calculate percent error chemistry",
      "percent error formula physics",
      "experimental vs theoretical error finder",
      "calculate percentage error lab",
      "absolute error calculator",
      "relative percentage error solver",
      "science lab error calculator",
    ],
  },
  {
    slug: "length-converter-calculator",
    name: "Length & Distance Converter",
    tagline: "Convert meters, kilometers, feet, inches, miles & yards instantly.",
    description: "Free length unit converter. Convert distance units between meters, kilometers, feet, inches, miles, yards, and centimeters with instant unit matrix conversions.",
    category: "Conversion & Geometry",
    icon: "Ruler",
    featured: true,
    keywords: [
      "length converter calculator",
      "convert feet to meters",
      "miles to kilometers converter",
      "inches to cm calculator",
      "distance unit converter",
      "yards to feet converter",
      "metric to imperial length converter",
      "unit length scaling finder",
    ],
  },
  {
    slug: "area-calculator",
    name: "2D Surface Area Calculator",
    tagline: "Calculate area of circles, rectangles, triangles & trapezoids.",
    description: "Free 2D surface area calculator. Calculate area for circles, rectangles, triangles, parallelograms, and trapezoids with step-by-step geometry formulas.",
    category: "Conversion & Geometry",
    icon: "Shapes",
    featured: true,
    keywords: [
      "area calculator geometry",
      "calculate area of circle pi r2",
      "rectangle area calculator",
      "triangle area solver 1/2 bh",
      "trapezoid surface area finder",
      "2d shape area formula calculator",
      "geometry area solver online",
      "perimeter and area calculator",
    ],
  },
  {
    slug: "volume-3d-calculator",
    name: "3D Volume Calculator",
    tagline: "Calculate volume of spheres, cylinders, cubes, cones & prisms.",
    description: "Free 3D volume calculator. Calculate volume and surface area for spheres, cylinders, cubes, rectangular prisms, and cones with step-by-step math.",
    category: "Conversion & Geometry",
    icon: "BoxSelect",
    featured: true,
    keywords: [
      "volume calculator 3d",
      "calculate volume of cylinder",
      "sphere volume formula calculator",
      "cube rectangular prism volume finder",
      "cone volume solver 3d shape",
      "surface area and volume calculator",
      "3d geometry volume solver",
      "cubic meter volume calculator",
    ],
  },
  {
    slug: "scientific-calculator",
    name: "Scientific Function Calculator",
    tagline: "Solve trig (sin, cos, tan), logarithms, square roots & exponents.",
    description: "Free online scientific calculator. Perform advanced scientific calculations including sine, cosine, tangent, natural logarithms (ln, log), square roots, and powers.",
    category: "Math & Algebra",
    icon: "Calculator",
    featured: true,
    keywords: [
      "scientific calculator online",
      "trigonometry calculator sin cos tan",
      "logarithm calculator ln log",
      "square root calculator online",
      "advanced math function solver",
      "scientific calculator with exponents",
      "rad vs deg trigonometry calculator",
      "web scientific calculator free",
    ],
  },
  {
    slug: "exponent-calculator",
    name: "Exponent & Power Calculator",
    tagline: "Calculate base raised to power (x^y), negative exponents & roots.",
    description: "Free exponent calculator. Calculate powers x^y, negative exponents, fractional exponents, and square/cube roots with step-by-step mathematical steps.",
    category: "Math & Algebra",
    icon: "Superscript",
    featured: true,
    keywords: [
      "exponent calculator",
      "calculate power x raised to y",
      "negative exponent solver",
      "fractional exponent calculator",
      "base and exponent power finder",
      "exponential growth calculator",
      "math powers solver",
      "square root and cube exponent calculator",
    ],
  },
  {
    slug: "gpa-to-4-scale-calculator",
    name: "Marks & 5-Point to 4.0 GPA Converter",
    tagline: "Convert 5-point scale CGPA or percentage marks to 4.0 GPA scale.",
    description: "Free GPA scale converter. Convert 5-point grading scales, 100-point marks, and international grade transcripts directly into standard 4.0 GPA scale points.",
    category: "Grade & Education",
    icon: "Award",
    featured: true,
    keywords: [
      "5 point gpa to 4.0 scale converter",
      "convert cgpa 5 to 4",
      "percentage to 4.0 gpa converter",
      "international grade converter 4.0 scale",
      "gpa scale converter online",
      "calculate 4.0 gpa from 100 marks",
      "college gpa scale mapping finder",
      "transcript 4.0 gpa converter",
    ],
  },
  {
    slug: "standard-deviation-calculator",
    name: "Standard Deviation & Variance Calculator",
    tagline: "Calculate sample/population standard deviation, variance & mean.",
    description: "Free online standard deviation calculator. Compute sample standard deviation (s), population standard deviation (σ), variance, mean, and sum of squares step-by-step.",
    category: "Statistics & Data",
    icon: "BarChart2",
    featured: true,
    keywords: [
      "standard deviation calculator",
      "calculate sample standard deviation",
      "population standard deviation formula",
      "variance calculator online statistics",
      "sample vs population variance solver",
      "mean and standard deviation tool",
      "calculate standard deviation step by step",
      "statistics standard deviation finder",
    ],
  },
  {
    slug: "probability-calculator",
    name: "Probability & Odds Calculator",
    tagline: "Calculate single event, compound P(A or B), P(A and B) & odds ratio.",
    description: "Free online probability calculator. Compute event probability, compound events (union and intersection), conditional probability, and odds ratios with instant math steps.",
    category: "Statistics & Data",
    icon: "PieChart",
    featured: true,
    keywords: [
      "probability calculator online",
      "calculate odds ratio statistics",
      "compound probability p a or b",
      "intersection probability p a and b",
      "conditional probability calculator",
      "single event probability solver",
      "how to calculate probability online",
      "stat probability finder",
    ],
  },
  {
    slug: "npr-ncr-calculator",
    name: "Permutation & Combination (nPr / nCr) Calculator",
    tagline: "Calculate permutations (nPr) and combinations (nCr) with factorials.",
    description: "Free nPr and nCr calculator. Compute permutations nPr and combinations nCr for n choose k with factorial formulas and optional replacement modes.",
    category: "Statistics & Data",
    icon: "Binary",
    featured: true,
    keywords: [
      "permutation and combination calculator",
      "ncr calculator n choose k",
      "npr calculator permutations",
      "factorial combination solver",
      "calculate npr and ncr online",
      "permutation formula calculator",
      "combination probability tool",
      "how to calculate ncr step by step",
    ],
  },
  {
    slug: "resistor-color-code-calculator",
    name: "Resistor Color Code Calculator",
    tagline: "Identify 4-band and 5-band resistor values, multipliers & tolerances.",
    description: "Free resistor color code calculator. Determine resistance value in Ohms (Ω), multiplier, and tolerance percentage for 4-band and 5-band electronic resistors.",
    category: "Engineering & Electronics",
    icon: "Cpu",
    featured: true,
    keywords: [
      "resistor color code calculator",
      "4 band resistor calculator",
      "5 band resistor code solver",
      "calculate resistor resistance ohms",
      "resistor color chart online",
      "electronic resistor color code tool",
      "resistor multiplier and tolerance finder",
      "how to read resistor color bands",
    ],
  },
  {
    slug: "voltage-drop-calculator",
    name: "Wire Voltage Drop & Size Calculator",
    tagline: "Calculate AC/DC wire voltage drop, AWG size & percentage loss.",
    description: "Free wire voltage drop calculator. Calculate voltage drop (Vdrop), percentage loss, and final voltage for single-phase and 3-phase AC/DC circuits by wire AWG gauge and length.",
    category: "Engineering & Electronics",
    icon: "Zap",
    featured: true,
    keywords: [
      "voltage drop calculator wire",
      "calculate wire voltage drop awg",
      "3 phase voltage drop calculator",
      "copper wire voltage drop solver",
      "electrical cable voltage drop formula",
      "ac dc voltage drop calculator",
      "voltage drop percentage loss finder",
      "how to calculate wire size voltage drop",
    ],
  },
  {
    slug: "power-factor-calculator",
    name: "Power Factor (PF) & kVAR Correction Calculator",
    tagline: "Calculate Power Factor (cos θ), kW, kVA, kVAR & capacitor rating.",
    description: "Free power factor calculator. Compute power factor (PF), real power (kW), apparent power (kVA), reactive power (kVAR), and required capacitor bank rating for power factor correction.",
    category: "Engineering & Electronics",
    icon: "Activity",
    featured: true,
    keywords: [
      "power factor calculator electrical",
      "calculate power factor pf kw kva",
      "kvar reactive power calculator",
      "power factor correction capacitor bank",
      "apparent power vs real power calculator",
      "3 phase power factor solver",
      "power triangle calculator kw kva kvar",
      "how to calculate power factor correction",
    ],
  },
  {
    slug: "molarity-calculator",
    name: "Molarity & Solution Concentration Calculator",
    tagline: "Calculate Molarity (M), solute mass (g), volume (L) & molar mass.",
    description: "Free chemistry molarity calculator. Compute molar concentration (M), solute mass in grams, solution volume in liters, and molar mass (g/mol) step-by-step.",
    category: "Chemistry & Science",
    icon: "FlaskConical",
    featured: true,
    keywords: [
      "molarity calculator chemistry",
      "calculate molarity formula",
      "moles to grams concentration solver",
      "solution volume molar concentration",
      "solute mass in grams calculator",
      "molar mass chemistry tool",
      "how to calculate molarity step by step",
      "lab concentration calculator online",
    ],
  },
  {
    slug: "ph-calculator",
    name: "pH, pOH & Hydrogen Ion [H+] Calculator",
    tagline: "Calculate pH, pOH, [H+] and [OH-] ion concentration levels.",
    description: "Free pH calculator chemistry. Calculate pH level, pOH, hydrogen ion concentration [H+], and hydroxide ion concentration [OH-] with instant acid-base steps.",
    category: "Chemistry & Science",
    icon: "TestTube",
    featured: true,
    keywords: [
      "ph calculator chemistry",
      "calculate h+ concentration from ph",
      "poh calculator acid base",
      "ph to hydroxide oh concentration",
      "solution ph level solver",
      "logarithmic ph calculator online",
      "acidic neutral basic ph finder",
      "how to calculate ph level step by step",
    ],
  },
  {
    slug: "ideal-gas-law-calculator",
    name: "Ideal Gas Law (PV = nRT) Calculator",
    tagline: "Calculate Pressure (P), Volume (V), Moles (n) or Temperature (T).",
    description: "Free ideal gas law calculator. Compute pressure (P), volume (V), moles of gas (n), or temperature (T) using PV = nRT with customizable gas constant R units.",
    category: "Chemistry & Science",
    icon: "Flame",
    featured: true,
    keywords: [
      "ideal gas law calculator pv nrt",
      "calculate gas pressure volume temperature",
      "gas constant r solver chemistry",
      "moles of gas ideal gas law",
      "kelvin temperature gas law tool",
      "thermodynamics ideal gas equation",
      "how to calculate ideal gas law",
      "gas law equation solver online",
    ],
  },
  {
    slug: "roi-calculator",
    name: "ROI (Return on Investment) Calculator",
    tagline: "Calculate ROI percentage (%), net profit ($) & annualized ROI.",
    description: "Free ROI calculator. Calculate return on investment percentage (%), net gain ($), and annualized ROI over investment holding periods with instant breakdown.",
    category: "Business & Productivity",
    icon: "TrendingUp",
    featured: true,
    keywords: [
      "roi calculator return on investment",
      "calculate roi percentage formula",
      "annualized roi calculator online",
      "net profit investment gain solver",
      "business investment roi finder",
      "real estate roi calculator",
      "how to calculate return on investment",
      "investment yield and profit tool",
    ],
  },
  {
    slug: "cpm-calculator",
    name: "CPM (Cost Per Mille / Thousand) Calculator",
    tagline: "Calculate CPM ad rate ($), campaign cost & ad impressions.",
    description: "Free CPM calculator for digital marketing. Calculate Cost Per Mille (CPM rate), total advertising budget cost ($), and required ad impressions in seconds.",
    category: "Business & Productivity",
    icon: "Target",
    featured: true,
    keywords: [
      "cpm calculator digital marketing",
      "cost per mille calculator ad rate",
      "calculate cpm ad campaign cost",
      "ad impressions cpm solver",
      "advertising cost per thousand tool",
      "facebook google ad cpm calculator",
      "how to calculate cpm ad rate",
      "media buying cpm cost finder",
    ],
  },
  {
    slug: "breakeven-calculator",
    name: "Breakeven Point & Profit Margin Calculator",
    tagline: "Calculate breakeven units, breakeven revenue & contribution margin.",
    description: "Free breakeven point calculator. Calculate breakeven sales volume in units, total breakeven revenue ($), fixed vs variable cost allocation, and contribution margin.",
    category: "Business & Productivity",
    icon: "DollarSign",
    featured: true,
    keywords: [
      "breakeven calculator business",
      "breakeven point formula units revenue",
      "contribution margin calculator",
      "fixed vs variable cost breakeven",
      "business profit breakeven solver",
      "startup breakeven point calculator",
      "how to calculate breakeven point",
      "sales volume breakeven analysis tool",
    ],
  },
  {
    slug: "mortgage-calculator",
    name: "Mortgage & Loan Amortization Calculator",
    tagline: "Calculate monthly mortgage payments, P&I, property tax, insurance & PMI.",
    description: "Free mortgage payment calculator with PMI and property tax. Calculate monthly home loan payments, total interest over 15 or 30 years, and annual amortization schedule breakdowns.",
    category: "Finance & Everyday",
    icon: "Home",
    featured: true,
    keywords: [
      "mortgage calculator",
      "home loan calculator",
      "monthly mortgage payment",
      "mortgage interest calculator",
      "mortgage payment calculator with pmi",
      "home loan amortization schedule finder",
      "monthly mortgage payment formula",
      "home purchase payment estimator",
      "principal and interest breakdown calculator",
    ],
  },
  {
    slug: "compound-interest-calculator",
    name: "Compound Interest & Savings Growth Calculator",
    tagline: "Calculate compounding growth, APY, monthly deposits & investment returns.",
    description: "Free compound interest calculator. Calculate future value of investments with monthly contributions, daily/monthly compounding frequencies, and year-by-year growth matrices.",
    category: "Finance & Everyday",
    icon: "TrendingUp",
    featured: true,
    keywords: [
      "compound interest calculator",
      "daily compound interest calculator",
      "savings growth calculator",
      "investment return calculator",
      "monthly contribution compound interest finder",
      "investment return calculator with monthly deposits",
      "future value of investment solver",
      "interest compounding schedule tool",
      "compound interest growth breakdown",
    ],
  },
  {
    slug: "tdee-calculator",
    name: "TDEE & BMR Calorie Calculator",
    tagline: "Calculate TDEE, BMR, maintenance calories & weight loss deficits.",
    description: "Free TDEE and BMR calculator. Compute Total Daily Energy Expenditure using Mifflin-St Jeor and Katch-McArdle formulas for weight loss, muscle gain, and macro splits.",
    category: "Health & Fitness",
    icon: "Flame",
    featured: true,
    keywords: [
      "tdee calculator",
      "bmr calculator",
      "total daily energy expenditure",
      "daily caloric intake",
      "bmr and tdee calculator online",
      "total daily energy expenditure solver",
      "calculate maintenance calories accurately",
      "mifflin st jeor bmr formula",
      "tdee calculation by activity level",
    ],
  },
  {
    slug: "inflation-calculator",
    name: "Inflation & Purchasing Power Calculator",
    tagline: "Calculate real money value changes, historical purchasing power & future costs.",
    description: "Free inflation calculator. Calculate how purchasing power changes over time, future equivalent dollar costs, and cumulative inflation loss percentage.",
    category: "Finance & Everyday",
    icon: "ArrowDownRight",
    featured: true,
    keywords: [
      "inflation calculator",
      "purchasing power calculator",
      "future value of money",
      "inflation rate calculator",
      "purchasing power loss calculator",
      "future money value inflation solver",
      "buying power converter over years",
      "cumulative inflation rate finder",
      "inflation adjusted dollar calculator",
    ],
  },
  {
    slug: "linear-equations-calculator",
    name: "System of Linear Equations Solver",
    tagline: "Solve 2x2 and 3x3 simultaneous linear equations using Cramer's Rule.",
    description: "Free system of linear equations calculator. Solve 2x2 and 3x3 simultaneous linear equations step-by-step using Cramer's rule determinants and substitution.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "linear equations solver",
      "system of equations calculator",
      "2x2 equation solver",
      "cramers rule calculator",
      "3x3 simultaneous equations solver",
      "cramers rule calculator step by step",
      "solve 2 linear equations online",
      "elimination method linear solver",
      "matrix system of equations solver",
    ],
  },
  {
    slug: "vector-calculator",
    name: "2D & 3D Vector Math Calculator",
    tagline: "Calculate vector dot product, cross product, magnitude, unit vectors & angles.",
    description: "Free 2D and 3D vector calculator. Compute vector addition, subtraction, dot product, cross product, magnitude |A|, unit vectors, and angles between vectors step-by-step.",
    category: "Unit & Physics",
    icon: "Compass",
    featured: true,
    keywords: [
      "vector calculator",
      "dot product calculator",
      "cross product calculator",
      "vector magnitude solver",
      "cross product calculator step by step",
      "vector magnitude solver 3d",
      "unit vector calculator online",
      "angle between two vectors finder",
      "2d and 3d vector component calculator",
    ],
  },
  {
    slug: "auto-loan-calculator",
    name: "Auto Loan & Car Payment Calculator",
    tagline: "Calculate monthly car loan payments, trade-in value, sales tax & interest.",
    description: "Free auto loan calculator with trade-in and sales tax. Calculate monthly car payments, total loan interest, and financing costs across 36, 48, 60, and 72-month terms.",
    category: "Finance & Everyday",
    icon: "Car",
    featured: true,
    keywords: [
      "auto loan calculator",
      "car payment calculator",
      "monthly car payment",
      "car loan interest calculator",
      "car loan calculator with trade in and tax",
      "auto loan monthly payment formula",
      "used car loan payment estimator",
      "car purchase total cost calculator",
      "auto loan interest breakdown finder",
    ],
  },
  {
    slug: "macro-ratio-calculator",
    name: "Macronutrient & Meal Ratio Calculator",
    tagline: "Calculate daily protein, carb & fat grams and per-meal macro breakdowns.",
    description: "Free macro ratio calculator. Calculate daily macronutrient grams for Keto, High Protein, Low Carb, or custom diet ratios with meal-by-meal macro breakdowns.",
    category: "Health & Fitness",
    icon: "Utensils",
    featured: true,
    keywords: [
      "macro calculator",
      "macronutrient ratio calculator",
      "flexible dieting macro split",
      "keto macro ratio calculator",
      "daily protein carb fat ratio solver",
      "macro breakdown per meal calculator",
      "custom macro ratio finder",
      "weight loss macronutrient target solver",
      "bodybuilding macro split calculator",
    ],
  },
  {
    slug: "percentage-change-calculator",
    name: "Percentage Change & Difference Calculator",
    tagline: "Calculate percent increase, decrease, percentage difference & modifiers.",
    description: "Free percentage change calculator. Compute percentage increase, percent decrease between two numbers, percentage difference, and percentage modifiers step-by-step.",
    category: "Math & Algebra",
    icon: "Percent",
    featured: true,
    keywords: [
      "percentage change calculator",
      "percent increase calculator",
      "percent decrease calculator",
      "percentage difference formula",
      "calculate percent difference between two numbers",
      "percentage increase and decrease solver",
      "relative percentage change finder",
      "percent change formula step by step",
      "percentage growth calculator online",
    ],
  },
  {
    slug: "fire-calculator",
    name: "FIRE & Retirement Nest Egg Calculator",
    tagline: "Calculate your Financial Independence Retire Early target & 4% rule nest egg.",
    description: "Free FIRE calculator. Compute your financial independence retire early target number based on the 4% rule, annual expenses, savings rate, and portfolio growth rate.",
    category: "Finance & Everyday",
    icon: "Flame",
    featured: true,
    keywords: [
      "fire calculator",
      "financial independence retire early calculator",
      "retirement savings calculator",
      "nest egg calculator",
      "fire number calculator 4 percent rule",
      "retirement withdrawal rate solver",
      "early retirement savings finder",
      "how much to save for fire retirement",
      "retirement portfolio growth estimator",
    ],
  },
  {
    slug: "loan-payoff-calculator",
    name: "Loan Payoff & Extra Payment Calculator",
    tagline: "Calculate interest savings and time reduced by extra monthly payments.",
    description: "Free loan payoff calculator. Calculate how much time and interest you save by making extra monthly payments towards mortgage, auto, or personal debt principal.",
    category: "Finance & Everyday",
    icon: "Sparkles",
    featured: true,
    keywords: [
      "loan payoff calculator",
      "extra loan payment calculator",
      "pay off loan early finder",
      "loan interest savings with extra payment",
      "mortgage extra payment calculator",
      "how much interest extra payments save",
      "early debt payoff solver",
      "loan term reduction calculator",
      "accelerated loan payoff tool",
    ],
  },
  {
    slug: "kinetic-energy-calculator",
    name: "Kinetic Energy & Momentum Physics Calculator",
    tagline: "Calculate kinetic energy (KE = ½mv²), linear momentum (p = mv) & velocity.",
    description: "Free kinetic energy calculator physics. Calculate kinetic energy in Joules, linear momentum p = mv, and velocity unit conversions step-by-step.",
    category: "Unit & Physics",
    icon: "Zap",
    featured: true,
    keywords: [
      "kinetic energy calculator",
      "momentum calculator physics",
      "calculate kinetic energy formula",
      "linear momentum solver p mv",
      "joules energy calculator",
      "mass and velocity kinetic energy finder",
      "physics energy and momentum calculator",
      "1/2 mv2 calculator step by step",
      "kinetic energy velocity solver",
    ],
  },
  {
    slug: "sales-tax-calculator",
    name: "Sales Tax & Reverse Tax Calculator",
    tagline: "Calculate sales tax addition, total prices & extract pre-tax baseline receipts.",
    description: "Free sales tax calculator. Calculate forward sales tax additions and reverse sales tax extractions to find original pre-tax baseline prices.",
    category: "Finance & Everyday",
    icon: "Tag",
    featured: true,
    keywords: [
      "sales tax calculator",
      "calculate sales tax",
      "reverse sales tax calculator",
      "after tax price calculator",
      "sales tax reverse finder",
      "calculate price before tax",
      "sales tax amount solver",
      "total price with tax estimator",
      "gross to net sales tax calculator",
    ],
  },
  {
    slug: "restaurant-tip-calculator",
    name: "Restaurant Tip & Bill Splitter Calculator",
    tagline: "Calculate tip percentages, total dining bills & per-person group splits.",
    description: "Free restaurant tip calculator and bill splitter. Calculate custom tip percentages, total dining bill with gratuity, and split payments evenly per person.",
    category: "Finance & Everyday",
    icon: "Utensils",
    featured: true,
    keywords: [
      "tip calculator",
      "split bill calculator",
      "restaurant tip calculator",
      "split bill with tip per person",
      "calculate tip amount per person",
      "group bill splitter online",
      "restaurant bill tip solver",
      "party bill split estimator",
      "tip percentage breakdown tool",
    ],
  },
  {
    slug: "gpa-conversion-calculator",
    name: "GPA, CGPA & 10-Point Scale Conversion Calculator",
    tagline: "Convert between 10.0 CGPA, 4.0 US GPA scale, percentage marks & letter grades.",
    description: "Free GPA conversion calculator. Convert 10.0 CGPA scale (CBSE 9.5 multiplier), 4.0 US GPA scale, and percentage marks (%) to letter grades.",
    category: "Grade & Education",
    icon: "Award",
    featured: true,
    keywords: [
      "gpa conversion calculator",
      "cgpa to percentage converter",
      "10 point gpa to percentage solver",
      "convert 4.0 gpa to percentage",
      "gpa to 10 point cgpa converter",
      "college gpa conversion tool",
      "international gpa to percentage finder",
      "gpa scale conversion matrix",
      "grade point percentage calculator",
    ],
  },
  {
    slug: "calorie-deficit-calculator",
    name: "Calorie Deficit & Weight Loss Target Date Calculator",
    tagline: "Calculate daily calorie allowances and project target goal weight completion dates.",
    description: "Free calorie deficit calculator. Calculate daily calorie targets for weight loss and project the exact estimated date to reach your target goal weight.",
    category: "Health & Fitness",
    icon: "Target",
    featured: true,
    keywords: [
      "calorie deficit calculator",
      "weight loss timeline calculator",
      "how long to lose weight calculator",
      "daily calorie deficit for weight loss",
      "target weight date solver",
      "calorie deficit goal calculator",
      "fat loss timeline estimator",
      "calorie deficit planner online",
      "weight loss duration finder",
    ],
  },
  {
    slug: "quadratic-solver-calculator",
    name: "Quadratic Equation & Parabola Vertex Solver",
    tagline: "Solve ax² + bx + c = 0 quadratic equations, real/complex roots & parabola vertex.",
    description: "Free quadratic equation solver. Calculate real and complex roots for ax² + bx + c = 0, discriminant Δ = b² - 4ac, and parabola vertex (h, k) step-by-step.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "quadratic equation solver",
      "quadratic formula calculator",
      "discriminant calculator b2 4ac",
      "vertex of parabola calculator",
      "find roots of quadratic equation step by step",
      "complex quadratic roots solver",
      "ax2 bx c equation calculator",
      "quadratic equation vertex finder",
      "parabola roots calculator online",
    ],
  },
  {
    slug: "work-power-calculator",
    name: "Work, Power & Horsepower Physics Calculator",
    tagline: "Calculate mechanical work (W = F·d·cos θ), power (Watts/kW) & horsepower (HP).",
    description: "Free work and power calculator physics. Calculate mechanical work in Joules W = F·d·cos θ, power in Watts (W, kW), and Horsepower (HP) step-by-step.",
    category: "Unit & Physics",
    icon: "Zap",
    featured: true,
    keywords: [
      "work and power calculator",
      "calculate physics work w fd",
      "power calculator watts horsepower",
      "physics work and energy solver",
      "force times distance calculator",
      "joules to watts power solver",
      "work done formula calculator step by step",
      "horsepower from watts calculator",
      "physics mechanical work solver",
    ],
  },
  {
    slug: "mortgage-refinance-calculator",
    name: "Mortgage Refinance & Break-Even Calculator",
    tagline: "Calculate monthly refinancing savings, closing cost break-even & interest.",
    description: "Free mortgage refinance calculator. Calculate monthly loan payment savings, closing cost break-even timelines, and net lifetime interest savings.",
    category: "Finance & Everyday",
    icon: "RefreshCw",
    featured: true,
    keywords: [
      "mortgage refinance calculator",
      "refinance break even calculator",
      "mortgage refinancing savings solver",
      "calculate refinancing monthly savings",
      "break even period refinance calculator",
      "closing cost refinance estimator",
      "home loan refinance calculator online",
      "refinance interest savings finder",
      "mortgage rate reduction calculator",
    ],
  },
  {
    slug: "ideal-body-weight-calculator",
    name: "Ideal Body Weight (IBW) Calculator",
    tagline: "Calculate ideal body weight using Devine, Robinson, Miller & Hamwi formulas.",
    description: "Free ideal body weight calculator. Compute medical ideal body weight using Devine, Robinson, Miller, and Hamwi formulas alongside healthy BMI weight ranges.",
    category: "Health & Fitness",
    icon: "HeartPulse",
    featured: true,
    keywords: [
      "ideal body weight calculator",
      "devine formula ideal weight",
      "hamwi ideal weight calculator",
      "healthy weight range finder",
      "ideal weight by height and age",
      "robinson formula body weight solver",
      "ideal body weight for men and women",
      "miller formula ideal weight calculator",
      "normal weight range calculator online",
    ],
  },
  {
    slug: "fraction-arithmetic-calculator",
    name: "Fraction & Mixed Number Arithmetic Calculator",
    tagline: "Perform fraction addition, subtraction, multiplication & division step-by-step.",
    description: "Free fraction calculator. Add, subtract, multiply, and divide proper, improper, and mixed fractions with step-by-step simplification and LCD math.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "fraction calculator",
      "mixed number calculator",
      "add fractions step by step",
      "subtracting fractions solver",
      "multiplying mixed numbers calculator",
      "dividing fractions calculator online",
      "improper fraction to mixed number solver",
      "least common denominator fraction calculator",
      "simplified fraction step by step tool",
    ],
  },
  {
    slug: "cagr-calculator",
    name: "Compound Annual Growth Rate (CAGR) Calculator",
    tagline: "Calculate annualized return percentage (CAGR %) & portfolio growth.",
    description: "Free CAGR calculator. Calculate compound annual growth rate percentage for investments, stocks, portfolio balances, and business revenue over multi-year horizons.",
    category: "Business & Productivity",
    icon: "TrendingUp",
    featured: true,
    keywords: [
      "cagr calculator",
      "compound annual growth rate calculator",
      "calculate cagr formula",
      "annual investment growth rate solver",
      "business revenue cagr finder",
      "portfolio cagr percentage calculator",
      "stock return cagr solver",
      "multi year growth rate calculator",
      "annualized return rate formula tool",
    ],
  },
  {
    slug: "wave-speed-calculator",
    name: "Wavelength, Frequency & Wave Speed Physics Calculator",
    tagline: "Calculate wave speed (v = f·λ), frequency (Hz) & wavelength (meters).",
    description: "Free wavelength and frequency calculator. Calculate wave speed v = f·λ, wave frequency in Hz, and wavelength for sound, light, and electromagnetic waves.",
    category: "Unit & Physics",
    icon: "Activity",
    featured: true,
    keywords: [
      "wavelength calculator",
      "frequency to wavelength calculator",
      "wave speed formula v f lambda",
      "light wave frequency solver",
      "sound wave speed calculator",
      "hertz to meters wavelength finder",
      "physics wave equation solver step by step",
      "electromagnetic wavelength calculator",
      "radio frequency wavelength tool",
    ],
  },
  {
    slug: "dti-calculator",
    name: "Debt-to-Income (DTI) Ratio Calculator",
    tagline: "Calculate front-end & back-end DTI percentage for mortgage lender approval.",
    description: "Free DTI calculator. Calculate front-end housing and back-end total debt-to-income percentages to check mortgage loan qualification limits.",
    category: "Finance & Everyday",
    icon: "ShieldCheck",
    featured: true,
    keywords: [
      "dti calculator",
      "debt to income ratio calculator",
      "calculate dti for mortgage",
      "front end vs back end dti solver",
      "mortgage qualification dti ratio finder",
      "debt to gross income percentage calculator",
      "how to calculate dti step by step",
      "lender dti limit checker",
      "monthly debt to income solver",
    ],
  },
  {
    slug: "paycheck-calculator",
    name: "Paycheck & Take-Home Pay Calculator",
    tagline: "Calculate gross-to-net take-home pay, federal/state taxes & paychecks.",
    description: "Free paycheck calculator. Calculate take-home pay per paycheck, federal and state tax withholdings, FICA taxes, and voluntary deductions for hourly and salary income.",
    category: "Finance & Everyday",
    icon: "Wallet",
    featured: true,
    keywords: [
      "paycheck calculator",
      "take home pay calculator",
      "gross to net pay calculator",
      "salary paycheck breakdown",
      "biweekly net pay calculator",
      "after tax paycheck estimator",
      "calculate paycheck taxes",
      "how much is my paycheck",
      "payroll net pay finder",
    ],
  },
  {
    slug: "water-retention-calculator",
    name: "Water Weight & Fluid Retention Calculator",
    tagline: "Analyze scale weight fluctuations caused by sodium, carbs & hydration.",
    description: "Free water weight calculator. Estimate fluid retention vs fat mass caused by sodium intake, glycogen storage, and daily water consumption.",
    category: "Health & Fitness",
    icon: "Droplets",
    featured: true,
    keywords: [
      "water weight calculator",
      "fluid retention calculator",
      "how much water weight am i holding",
      "bloating water weight finder",
      "sodium fluid retention solver",
      "calculate water vs fat weight",
      "rapid weight gain water weight",
      "how to drop water weight fast",
      "fluid balance estimator",
    ],
  },
  {
    slug: "polynomial-calculator",
    name: "Polynomial Solver & Synthetic Division Calculator",
    tagline: "Evaluate cubic polynomials f(x), derivatives & synthetic division step-by-step.",
    description: "Free polynomial calculator. Evaluate cubic polynomials f(x) = ax³ + bx² + cx + d, compute derivatives f'(x), and perform synthetic division with quotient tables.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "polynomial calculator",
      "synthetic division calculator",
      "polynomial roots solver",
      "cubic equation solver step by step",
      "find roots of polynomial",
      "polynomial degree and factors",
      "quartic equation solver",
      "algebra synthetic division step by step",
      "polynomial evaluation calculator",
    ],
  },
  {
    slug: "car-depreciation-calculator",
    name: "Car Depreciation & Resale Value Calculator",
    tagline: "Estimate vehicle resale value loss over 1 to 10 years based on mileage.",
    description: "Free car depreciation calculator. Calculate how much value your vehicle loses over 1 to 10 years based on purchase price, vehicle category, and annual mileage.",
    category: "Finance & Everyday",
    icon: "Car",
    featured: true,
    keywords: [
      "car depreciation calculator",
      "auto resale value calculator",
      "how much value does a car lose",
      "used car depreciation rate",
      "5 year car value calculator",
      "vehicle depreciation schedule",
      "car value loss per year",
      "calculate car depreciation online",
      "car trade in value depreciation",
    ],
  },
  {
    slug: "weighted-gpa-calculator",
    name: "Weighted vs Unweighted GPA Calculator",
    tagline: "Calculate weighted GPA with AP (+1.0) & Honors (+0.5) class grade boosts.",
    description: "Free weighted GPA calculator. Calculate weighted GPA and unweighted 4.0 GPA with AP, IB, Honors, and standard course level difficulty weight boosts.",
    category: "Grade & Education",
    icon: "GraduationCap",
    featured: true,
    keywords: [
      "weighted gpa calculator",
      "ap honors gpa calculator",
      "weighted vs unweighted gpa calculator",
      "high school weighted gpa finder",
      "calculate weighted gpa step by step",
      "ap class gpa boost calculator",
      "college weighted gpa solver",
      "honors gpa converter",
      "overall weighted gpa finder",
    ],
  },
  {
    slug: "torque-calculator",
    name: "Torque & Rotational Dynamics Physics Calculator",
    tagline: "Calculate rotational force (τ = r F sin θ), angular acceleration & power.",
    description: "Free physics torque calculator. Calculate rotational torque τ = r F sin θ, angular acceleration α, rotational kinetic energy, and horsepower output.",
    category: "Unit & Physics",
    icon: "RotateCw",
    featured: true,
    keywords: [
      "torque calculator",
      "rotational physics calculator",
      "calculate torque tau r f sin theta",
      "moment of inertia torque solver",
      "angular acceleration and torque calculator",
      "rotational kinetic energy solver",
      "torque to power horsepower calculator",
      "physics torque equation solver",
      "rotational motion calculator",
    ],
  },
  {
    slug: "half-life-calculator",
    name: "Radioactive Half-Life & Decay Calculator",
    tagline: "Calculate remaining quantity N(t), decay constant λ & half-life elapsed.",
    description: "Free radioactive half-life calculator. Calculate remaining quantity N(t), decay constant λ = ln(2)/t½, and half-life steps for chemistry and physics.",
    category: "Chemistry & Science",
    icon: "Atom",
    featured: true,
    keywords: [
      "half life calculator",
      "radioactive decay calculator",
      "calculate half life formula",
      "decay constant lambda finder",
      "isotope half life calculator",
      "half life equation solver",
      "carbon dating calculator chemistry",
      "exponential decay rate solver",
      "remaining mass half life",
    ],
  },
  {
    slug: "amortization-schedule-calculator",
    name: "Loan & Mortgage Amortization Schedule Calculator",
    tagline: "Generate annual & monthly principal vs interest loan amortization tables.",
    description: "Free loan amortization schedule calculator. Generate annual and monthly principal vs interest repayment schedules and calculate extra payment savings.",
    category: "Finance & Everyday",
    icon: "Table",
    featured: true,
    keywords: [
      "amortization schedule calculator",
      "loan amortization table",
      "mortgage amortization schedule",
      "calculate monthly principal and interest",
      "yearly amortization breakdown",
      "extra payment interest savings calculator",
      "loan repayment schedule finder",
      "principal balance over time",
      "home loan schedule solver",
    ],
  },
  {
    slug: "bmi-percentile-calculator",
    name: "Child & Teen BMI Percentile Calculator (CDC & WHO)",
    tagline: "Calculate pediatric BMI-for-age percentile & health category for kids 2-19.",
    description: "Free child BMI percentile calculator. Calculate pediatric BMI-for-age percentile for boys and girls according to CDC growth chart standards.",
    category: "Health & Fitness",
    icon: "HeartPulse",
    featured: true,
    keywords: [
      "bmi percentile calculator",
      "child bmi calculator cdc",
      "pediatric bmi percentile calculator",
      "teen bmi for age calculator",
      "calculate child bmi percentile",
      "who child growth percentile finder",
      "kids bmi category solver",
      "healthy weight range for kids",
      "child growth chart bmi",
    ],
  },
  {
    slug: "matrix-determinant-calculator",
    name: "Matrix Determinant & Transpose Calculator",
    tagline: "Calculate 2x2 and 3x3 matrix determinants det(A), transpose Aᵀ & invertibility.",
    description: "Free matrix determinant calculator. Calculate 2x2 and 3x3 determinants det(A), transpose matrices Aᵀ, and test matrix invertibility.",
    category: "Math & Algebra",
    icon: "Grid",
    featured: true,
    keywords: [
      "matrix determinant calculator",
      "inverse matrix calculator",
      "3x3 matrix determinant solver",
      "4x4 determinant calculator",
      "cofactor matrix solver",
      "calculate matrix determinant step by step",
      "linear algebra matrix inverse",
      "matrix transpose calculator",
      "determinant formula solver",
    ],
  },
  {
    slug: "calories-burned-calculator",
    name: "Exercise Calories Burned & MET Calculator",
    tagline: "Calculate workout calories burned for running, cycling, swimming & HIIT.",
    description: "Free calories burned calculator. Calculate exercise calorie expenditure using standard Metabolic Equivalent of Task (MET) rates based on weight and duration.",
    category: "Health & Fitness",
    icon: "Flame",
    featured: true,
    keywords: [
      "calories burned calculator",
      "exercise calories burned finder",
      "running calories burned calculator",
      "metabolic equivalent met calculator",
      "walking calories burned by weight",
      "workout calorie burn estimator",
      "how many calories did i burn",
      "cycling calories burned solver",
      "hiit calorie burn calculator",
    ],
  },
  {
    slug: "electricity-cost-calculator",
    name: "Appliance Electricity & Power Cost Calculator",
    tagline: "Calculate appliance power usage in kWh and electricity cost per day, month & year.",
    description: "Free electricity cost calculator. Compute appliance power usage in kilowatt-hours (kWh) and calculate monthly and annual electric bill costs.",
    category: "Finance & Everyday",
    icon: "Zap",
    featured: true,
    keywords: [
      "electricity cost calculator",
      "appliance power consumption calculator",
      "calculate energy cost per kwh",
      "watt to kwh electricity cost",
      "monthly electric bill estimator",
      "appliance wattage cost finder",
      "power usage cost solver",
      "how much electricity does an appliance use",
      "kilowatt hour cost calculator",
    ],
  },
  {
    slug: "dilution-calculator",
    name: "Solution Dilution Calculator (C₁V₁ = C₂V₂)",
    tagline: "Calculate required stock volume V₁, concentration C₂ & solvent addition.",
    description: "Free solution dilution calculator. Compute stock volume V₁, target concentration C₂, and solvent volume needed using C₁V₁ = C₂V₂.",
    category: "Chemistry & Science",
    icon: "FlaskConical",
    featured: true,
    keywords: [
      "dilution calculator",
      "c1v1 c2v2 calculator",
      "solution dilution calculator",
      "how to calculate dilution chemistry",
      "molarity dilution calculator",
      "concentration dilution solver",
      "stock solution dilution calculator",
      "lab solution volume solver",
      "dilution factor calculator",
    ],
  },
  {
    slug: "rc-circuit-calculator",
    name: "RC Circuit Time Constant (τ = R·C) Calculator",
    tagline: "Calculate RC time constant τ, capacitor charging voltage V(t) & cutoff frequency.",
    description: "Free RC circuit time constant calculator. Compute time constant τ = R·C, capacitor charging voltage V(t), and 5-tau milestone tables.",
    category: "Engineering & Electronics",
    icon: "Cpu",
    featured: true,
    keywords: [
      "rc circuit calculator",
      "rc time constant calculator",
      "tau rc formula calculator",
      "capacitor charging voltage calculator",
      "resistor capacitor time constant",
      "rc filter cutoff frequency solver",
      "capacitor charge curve solver",
      "electrical rc circuit tool",
      "time constant tau solver",
    ],
  },
  {
    slug: "gpa-semester-calculator",
    name: "Semester & Term GPA Calculator",
    tagline: "Calculate current semester GPA and project updated overall cumulative CGPA.",
    description: "Free semester GPA calculator. Compute term grade point average and project updated overall cumulative CGPA across college courses.",
    category: "Grade & Education",
    icon: "GraduationCap",
    featured: true,
    keywords: [
      "semester gpa calculator",
      "term gpa calculator",
      "college semester gpa finder",
      "calculate semester gpa step by step",
      "projected cumulative gpa calculator",
      "current term grade calculator",
      "university semester gpa solver",
      "semester grade point average tool",
      "predict cumulative gpa",
    ],
  },
  {
    slug: "stoichiometry-calculator",
    name: "Stoichiometry & Theoretical Yield Calculator",
    tagline: "Calculate reactant moles, mole ratio & product theoretical yield grams.",
    description: "Free chemistry stoichiometry calculator. Calculate reactant moles, stoichiometric mole ratios, and product theoretical yield in grams.",
    category: "Chemistry & Science",
    icon: "FlaskConical",
    featured: true,
    keywords: [
      "stoichiometry calculator",
      "theoretical yield calculator chemistry",
      "molar mass stoichiometry solver",
      "calculate limiting reactant stoichiometry",
      "grams to moles chemistry solver",
      "chemical reaction yield calculator",
      "stoichiometric mole ratio calculator",
      "mass to mass stoichiometry solver",
      "reaction yield finder",
    ],
  },
  {
    slug: "logic-gate-calculator",
    name: "Boolean Logic Gate & Truth Table Generator",
    tagline: "Evaluate digital logic gates (AND, OR, NAND, NOR, XOR, XNOR) & truth tables.",
    description: "Free logic gate calculator. Evaluate digital logic gates (AND, OR, NAND, NOR, XOR, XNOR) and generate complete 4-row binary truth tables.",
    category: "Engineering & Electronics",
    icon: "Cpu",
    featured: true,
    keywords: [
      "logic gate calculator",
      "truth table generator online",
      "boolean logic gate solver",
      "xor gate truth table calculator",
      "nand nor logic calculator",
      "digital electronics truth table",
      "boolean algebra expression solver",
      "logic circuit calculator",
      "binary logic gate finder",
    ],
  },
  {
    slug: "ellipse-calculator",
    name: "Ellipse Area, Perimeter & Foci Calculator",
    tagline: "Calculate ellipse area (A = π a b), Ramanujan perimeter, foci & eccentricity.",
    description: "Free ellipse calculator. Calculate ellipse area A = π a b, Ramanujan perimeter approximation, focal distance c, and eccentricity e.",
    category: "Conversion & Geometry",
    icon: "Shapes",
    featured: true,
    keywords: [
      "ellipse calculator",
      "ellipse area calculator",
      "ellipse perimeter calculator",
      "foci of ellipse calculator",
      "eccentricity of ellipse finder",
      "semi major semi minor axis solver",
      "ramanujan ellipse perimeter formula",
      "geometry ellipse solver online",
      "ellipse focal distance calculator",
    ],
  },
  {
    slug: "buffer-ph-calculator",
    name: "Henderson-Hasselbalch Buffer pH Calculator",
    tagline: "Calculate acid-base buffer pH, pKa & hydrogen ion [H+] concentration.",
    description: "Free buffer pH calculator. Calculate acid-base buffer solution pH using Henderson-Hasselbalch equation pH = pKa + log10([A-]/[HA]).",
    category: "Chemistry & Science",
    icon: "TestTube",
    featured: true,
    keywords: [
      "buffer ph calculator",
      "henderson hasselbalch calculator",
      "calculate buffer ph formula",
      "weak acid conjugate base ph finder",
      "pka to ph calculator chemistry",
      "acid base buffer ratio solver",
      "henderson hasselbalch equation solver",
      "chemistry buffer capacity calculator",
      "ph of buffer solution solver",
    ],
  },
  {
    slug: "led-resistor-calculator",
    name: "LED Series Current Limiting Resistor Calculator",
    tagline: "Calculate LED resistor value (R = (V_S - V_F)/I_F), standard E24 ohms & wattage.",
    description: "Free LED resistor calculator. Compute LED current-limiting resistor ohms, nearest standard E24 resistor value, and resistor power dissipation watts.",
    category: "Engineering & Electronics",
    icon: "Zap",
    featured: true,
    keywords: [
      "led resistor calculator",
      "current limiting resistor calculator",
      "calculate led resistor ohms",
      "led forward voltage current solver",
      "led series resistor formula",
      "standard resistor value for led",
      "resistor power dissipation watts",
      "diode series resistor finder",
      "electronic led circuit calculator",
    ],
  },
  {
    slug: "gpa-raise-calculator",
    name: "GPA Raising & Target Goal Calculator",
    tagline: "Calculate future semester average GPA required to reach your target GPA goal.",
    description: "Free target GPA calculator. Calculate future semester GPA required to raise your cumulative GPA to your target goal (e.g. 3.5 or 3.8 GPA).",
    category: "Grade & Education",
    icon: "Target",
    featured: true,
    keywords: [
      "gpa raise calculator",
      "how to raise my gpa calculator",
      "required gpa to reach target",
      "target cumulative gpa solver",
      "can i raise my gpa to 3.5",
      "calculate needed gpa for future semesters",
      "raise college gpa finder",
      "desired gpa goal calculator",
      "gpa recovery calculator",
    ],
  },
  {
    slug: "polynomial-roots-calculator",
    name: "Polynomial Roots & Quadratic/Cubic Factoring Calculator",
    tagline: "Find real & complex roots, discriminant Δ & vertex for ax³ + bx² + cx + d = 0.",
    description: "Free polynomial roots calculator. Calculate real and complex roots for quadratic and cubic equations ax³ + bx² + cx + d = 0.",
    category: "Math & Algebra",
    icon: "Binary",
    featured: true,
    keywords: [
      "polynomial roots calculator",
      "find roots of cubic equation",
      "quadratic cubic roots solver",
      "real and complex roots calculator",
      "polynomial zero finder step by step",
      "solve ax3 bx2 cx d 0",
      "algebraic root factoring tool",
      "polynomial discriminant calculator",
      "roots of equation solver online",
    ],
  },
  {
    slug: "decibel-calculator",
    name: "Decibel (dB) Sound & Power Gain Calculator",
    tagline: "Calculate decibel gain/loss (dB) for power (10 log) and voltage/sound (20 log).",
    description: "Free decibel calculator. Compute decibel gain/attenuation dB = 10 log(P2/P1) for power and dB = 20 log(V2/V1) for voltage and sound pressure.",
    category: "Engineering & Electronics",
    icon: "Volume2",
    featured: true,
    keywords: [
      "decibel calculator",
      "db power gain calculator",
      "voltage ratio to db converter",
      "sound pressure level spl calculator",
      "calculate decibels db formula",
      "attenuation gain in db solver",
      "audio decibel gain calculator",
      "power ratio to decibels finder",
      "acoustics db calculator",
    ],
  },
  {
    slug: "empirical-formula-calculator",
    name: "Empirical & Molecular Formula Chemistry Calculator",
    tagline: "Calculate simplest mole ratios, empirical formula & molecular formula.",
    description: "Free empirical formula calculator. Calculate simplest mole ratios, empirical formula (CH₂O), and true molecular formula (C₆H₁₂O₆) from mass percent.",
    category: "Chemistry & Science",
    icon: "FlaskConical",
    featured: true,
    keywords: [
      "empirical formula calculator",
      "molecular formula calculator chemistry",
      "calculate empirical formula from percent composition",
      "simplest mole ratio finder",
      "percent composition to empirical formula",
      "molar mass to molecular formula solver",
      "chemistry empirical formula solver",
      "element mass percent formula tool",
      "empirical vs molecular formula solver",
    ],
  },
  {
    slug: "customer-lifetime-value-calculator",
    name: "Customer Lifetime Value (LTV / CLV) Calculator",
    tagline: "Calculate gross & net customer LTV, AOV, churn, & LTV:CAC payback ratio.",
    description: "Free Customer Lifetime Value calculator. Calculate gross LTV, net customer revenue, customer lifespan value, monthly LTV, and evaluate LTV:CAC ratio.",
    category: "Business & Productivity",
    icon: "Users",
    featured: true,
    keywords: [
      "clv calculator",
      "customer lifetime value calculator",
      "ltv calculator",
      "ltv cac ratio calculator",
      "calculate customer lifetime value",
      "how to calculate clv",
      "saas ltv calculator",
      "customer value formula",
      "e commerce clv calculator",
    ],
  },
  {
    slug: "freelance-hourly-rate-calculator",
    name: "Freelance Hourly & Daily Rate Calculator",
    tagline: "Calculate required hourly rate based on salary target, overhead expenses & taxes.",
    description: "Free freelance rate calculator. Calculate minimum hourly billing rate and daily client rate based on desired salary, billable hours, business overhead, and taxes.",
    category: "Business & Productivity",
    icon: "Briefcase",
    featured: true,
    keywords: [
      "freelance hourly rate calculator",
      "freelance rate calculator",
      "calculate hourly rate freelancer",
      "contractor daily rate calculator",
      "how much to charge as a freelancer",
      "billable hours rate calculator",
      "freelance salary calculator",
      "consultant hourly rate finder",
      "freelance pricing calculator",
    ],
  },
  {
    slug: "employee-turnover-cost-calculator",
    name: "Employee Turnover & Replacement Cost Calculator",
    tagline: "Calculate true cost of staff departures, recruiting, onboarding & lost productivity.",
    description: "Free employee turnover cost calculator. Calculate recruiting fees, onboarding expenses, lost productivity, and annual team resignation costs.",
    category: "Business & Productivity",
    icon: "UserMinus",
    featured: true,
    keywords: [
      "turnover cost calculator",
      "employee turnover cost calculator",
      "calculate cost of employee turnover",
      "hiring replacement cost calculator",
      "cost per hire calculator",
      "staff retention cost finder",
      "hr employee turnover calculator",
      "cost of losing an employee",
      "employee departure cost calculator",
    ],
  },
  {
    slug: "customer-acquisition-cost-calculator",
    name: "Customer Acquisition Cost (CAC) & Payback Calculator",
    tagline: "Calculate CAC per customer, sales & marketing efficiency, & payback months.",
    description: "Free Customer Acquisition Cost (CAC) calculator. Calculate CAC per customer, marketing ad spend efficiency, sales overhead, and CAC payback period.",
    category: "Business & Productivity",
    icon: "Target",
    featured: true,
    keywords: [
      "cac calculator",
      "customer acquisition cost calculator",
      "calculate cac",
      "cac payback period calculator",
      "marketing acquisition cost calculator",
      "saas cac calculator",
      "how to calculate customer acquisition cost",
      "ad spend customer cost finder",
      "sales acquisition cost solver",
    ],
  },
  {
    slug: "working-capital-calculator",
    name: "Working Capital & Current Ratio Calculator",
    tagline: "Calculate net working capital, current ratio, quick ratio & business liquidity.",
    description: "Free working capital calculator. Calculate net working capital, current ratio, quick ratio (acid test), and evaluate business liquidity health.",
    category: "Business & Productivity",
    icon: "Landmark",
    featured: true,
    keywords: [
      "working capital calculator",
      "calculate working capital",
      "current ratio calculator",
      "quick ratio calculator",
      "working capital formula",
      "business liquidity ratio calculator",
      "acid test ratio calculator",
      "net working capital solver",
      "corporate liquidity calculator",
    ],
  },
  {
    slug: "email-open-rate-click-through-calculator",
    name: "Email Marketing Open Rate, CTR & Campaign ROI Calculator",
    tagline: "Calculate email open rate %, CTR %, CTOR %, conversion rate & campaign net ROI.",
    description: "Free email marketing metrics calculator. Calculate open rate %, click-through rate (CTR), click-to-open rate (CTOR), conversion rate, and email campaign ROI.",
    category: "Business & Productivity",
    icon: "Mail",
    featured: true,
    keywords: [
      "email open rate calculator",
      "ctr calculator email",
      "email click through rate calculator",
      "ctor calculator email marketing",
      "email marketing roi calculator",
      "calculate email open rate",
      "email campaign conversion calculator",
      "email click to open rate finder",
      "newsletter open rate calculator",
    ],
  },
];

export function getCalculatorBySlug(slug: string): CalculatorMeta | undefined {
  return CALCULATORS.find((c) => c.slug === slug);
}

export function relatedCalculators(currentSlug: string, count = 4): CalculatorMeta[] {
  return CALCULATORS.filter((c) => c.slug !== currentSlug).slice(0, count);
}
